import { useMutation } from '@apollo/client';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import { AlertDismissible } from '@website/components/utilities/AlertDismissible';
import ErrorPage from '@website/components/utilities/ErrorPage';
import {
  TripDetailsForTripEditFormQuery,
  UpdateTripDetailsDocument,
  UpdateTripInput,
} from '@website/types/graphqlOperations';
import Router from 'next/router';
import { useCallback, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import ExcludesFields from './Inclussions/ExcludesFields';
import IncludesFields from './Inclussions/IncludesFields';

type InclusionsFormProps = {
  trip: TripDetailsForTripEditFormQuery['tripByURL'];
};

export type InclusionFormType = {
  includes?: string[];
  excludes?: string[];
};

export default function InclusionsForm({ trip }: InclusionsFormProps) {
  const [successMessage, setSuccessMessage] = useState<string>();
  const methods = useForm<InclusionFormType>({
    defaultValues: {
      includes: trip?.includes,
      excludes: trip?.excludes,
    },
  });

  const [updateTripFN, { data, error }] = useMutation(
    UpdateTripDetailsDocument,
  );

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = useCallback(
    async (formData: InclusionFormType) => {
      const updateTrip: UpdateTripInput = {
        id: trip.id,
        active: trip.active === 'ACTIVE',
        address: trip.address,
        description: trip.description,
        endTimezone: trip.endTimezone,
        hostID: trip.host.id,
        image: {
          id: trip.image.id,
        },
        startTimezone: trip.startTimezone,
        difficultyLevel: trip.difficultyLevel,
        title: trip.title,
        urlSlug: trip.urlSlug,
        activities: trip.activities,
        notice: trip.notice,
        tripVideoURL: trip.tripVideoURL,
        tripVideoURLThumbnail: trip.tripVideoURLThumbnail
          ? {
              id: trip.tripVideoURLThumbnail?.id,
            }
          : undefined,
        featuredImages: trip.featuredImages?.map((image) => ({
          id: image.id,
        })),
        meals: trip.meals,
        // the following fields are the only ones that are updated
        includes: formData.includes,
        excludes: formData.excludes,
        allowPrivateGroups: trip.allowPrivateGroups,
      };

      await updateTripFN({
        variables: {
          input: updateTrip,
        },
      });

      setSuccessMessage('Trip updated successfully');
    },
    [trip, updateTripFN],
  );

  const onDismissAlert = () => {
    // redirect only new trips
    if (!trip || !trip.id) {
      Router.push(`/trips/${data?.updateTrip.urlSlug}`);
    }
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      {successMessage && (
        <Row>
          <Col>
            <AlertDismissible
              onClose={() => onDismissAlert()}
              variant="success"
              title="Trip Edit Form"
              message={successMessage}
            />
          </Col>
        </Row>
      )}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Row>
            <Col xs={12}>
              <IncludesFields />
            </Col>

            <Col xs={12}>
              <ExcludesFields />
            </Col>

            <Col xs={12}>
              <SubmitButton isSubmitting={isSubmitting} />
            </Col>
          </Row>
        </FormProvider>
      </Form>
    </>
  );
}
